import React, { useState, useEffect } from "react";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import { determineUserAccess } from "../../../../core/permissions/userLevelAccess";
import { PageContentLayout, PageHeaderLayout } from "../../../shared/layout";
import { AppstoreAddOutlined } from "@ant-design/icons";
import { Card, message, Tabs, Row, Typography, Spin } from "antd";
import CreativeTab from "../../OrgConfig/components/CreativeTab";
import CreativeTable from "../../OrgConfig/components/CreativeTable";
import CreativeAdGroupTable from "../../OrgConfig/components/CreativeAdGroupTable";
import VideoTab from "../../OrgConfig/components/VideoTab";
import VideoTable from "../../OrgConfig/components/VideoTable";
import AdVideosGroupTable from "../../OrgConfig/components/AdVideosGroupTable";
import { errorType } from "../../../../core/utils/constants/constants";
import "./../library/creativestyles";
import { useAssignAdUnits } from "../hooks/assignAdUnits";

// NEW IMPORTS - Enhanced navigation system
import { useCreativeNavigation } from "../hooks/useCreativeNavigation";
import { usePaginationState } from "../hooks/usePaginationState";
import { useRouteValidation } from "../hooks/useRouteValidation";

// Removed TabPane destructuring - using items prop instead

const LibraryCreativesView = () => {
  // Set page title
  document.title = "Creatives Main View";


  // NEW: Enhanced navigation hooks
  const { 
    currentState, 
    navigateToTab, 
    navigateToPage,
    getNavigationHelpers 
  } = useCreativeNavigation();
  
  const {
    updateCurrentPagination
  } = usePaginationState();
  
  
  // NEW: Route validation with custom error handling
  const { isRouteValid } = useRouteValidation({
    autoRedirect: true,
    onInvalidRoute: (errors) => {
      console.warn('Redirecting from invalid creative route:', errors);
      message.warning('Invalid page parameters, redirecting to default view');
    }
  });

  // Context and permissions
  const {
    loggedInOrg,
    updateManyMedias,
    getAdGroupsByMedia,
    deleteAdGroupCreatives,
    currentuser,
    createAdGroupCreatives,
    adGroupCreatives,
    refetchMedias,
    updateOrgMedia,
    refetchVideoMedias,
    orgVideoMedias,
    updateManyAdGroupCreatives,
    updateAdGroupCreatives,
    createManyOrgMedia,
    setDefaultAmplifyCreatives,
    handleRowClick,
  } = useLibraryCreativesContext() || {};

  // NEW: Get navigation helpers for current state
  const { isDisplayAds, isVideoAds } = getNavigationHelpers();

  // State management
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [creativeName, setCreativeName] = useState("");
  const [onLoadingDeleteMedias, setOnLoadingDeleteMedias] = useState(false);
  const [onLoadingDeleteAdGroup, setOnLoadingDeleteAdGroup] = useState(false);
  const [loadingDisplayCreatives, setLoadingDisplayCreatives] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [ids, setIds] = useState([]);
  const [renewedCreatives, setRenewedCreatives] = useState([]);
  const [creativeKeys, setCreativeKeys] = useState([]);
  const [videoCreativeIds, setVideoCreativeIds] = useState([]);
  const [videoCreativeKeys, setVideoCreativeKeys] = useState([]);

  const { permission } = currentuser || {};
  const userPermission = permission ? determineUserAccess(permission) : {};
  const { amplify } = userPermission || {};
  const { Title } = Typography;
  const { handleAssignAdUnits, assigning } = useAssignAdUnits();

  const handleSearch = searchText => {
    setSearchText(searchText);
  };

  // State to store selected media for ad group creation
  const [adGroupMediasState, setAdGroupMediasState] = useState([]);

  const onChangeStateValue = (key, val) => {
    switch (key) {
      case "creativeName":
        setCreativeName(val);
        break;
      case "adGroupMedias":
        setAdGroupMediasState(val);
        break;
      case "onLoadingDeleteMedias":
        setOnLoadingDeleteMedias(val);
        break;
      case "onLoadingDeleteAdGroup":
        setOnLoadingDeleteAdGroup(val);
        break;
      default:
        break;
    }
  };

  const onDeleteMedias = async ids => {
    try {
      message.loading("Action in progress..", 0);
      setOnLoadingDeleteMedias(true);

      await updateManyMedias({
        variables: {
          ids,
          data: { flag: false },
        },
      });

      message.destroy();
      message.success(`${ids.length} creative${ids.length === 1 ? '' : 's'} successfully deleted.`);
      setOnLoadingDeleteMedias(false);

      const adGroupCreativesResult = await getAdGroupsByMedia({ media: ids, flag: true });

      const mappedAdGroupCreativeIds = (adGroupCreativesResult?.data?.adGroupCreativeses || [])
        .filter(obj => obj?.medias && Array.isArray(obj.medias) && obj.medias.every(media => !media.flag))
        .map(obj => obj.id);

      if (mappedAdGroupCreativeIds.length > 0) {
        mappedAdGroupCreativeIds.forEach(x =>
          deleteAdGroupCreatives({ variables: { id: x } })
        );
      }
      
      return ids;
    } catch (err) {
      message.destroy();
      setOnLoadingDeleteMedias(false);
      if (err.message === errorType.deleteMedia) {
        message.error("Cannot delete campaign - it is already synced with a creative.");
      } else {
        message.error("Failed to delete creatives. Please try again.");
      }
      throw err;
    }
  };

  const onCreateAdgroup = async (customName = null, customMedias = null) => {
    const {
      role,
      permission: { amplify },
    } = currentuser;

    const orgs = [role.org.id];
    const groupName = customName || creativeName;
    const groupMedias = customMedias || adGroupMediasState;

    try {
      message.loading("Action in progress..", 0);

      const result = await createAdGroupCreatives({
        variables: {
          name: groupName,
          medias: groupMedias,
          org: orgs.map(o => ({ id: o })),
        },
      });

      // Check if this should be set as amplify default after creation
      // Check if there are no existing ad groups with amplify default
      const hasExistingAmplifyDefault = adGroupCreatives && adGroupCreatives.some(ag => ag.isAmplifyDefault);
      if (amplify && !hasExistingAmplifyDefault) {
        // This should be set as the amplify default
        const createdAdGroup = result?.data?.createAdGroupCreatives;
        if (createdAdGroup) {
          setAdGroupAsAmplifyDefault(createdAdGroup);
        }
      }

      message.destroy();
      message.success('Successfully Created Ad Group');

      // Clear state immediately for fast UI response
      setIds([]);
      setVideoCreativeIds([]);
      setCreativeName("");
      setAdGroupMediasState([]);

      // Refresh data in background without blocking modal close
      refetchMedias().catch(refreshError => {
        console.warn('Failed to refresh media data:', refreshError);
      });
    } catch (err) {
      message.destroy();
      message.error('Failed to create ad group. Please try again.');
    }
  };

  const setAdGroupAsAmplifyDefault = async record => {
    await updateManyAdGroupCreatives({
      variables: {
        orgId: record.org[0].id,
        isAmplifyDefault: false,
      },
    });

    await updateAdGroupCreatives({
      variables: {
        id: record.id,
        data: { isAmplifyDefault: true },
      },
    });
  };

  const bulkCreateOrgMedia = async (
    medias,
    invalidMedias,
    calledFromOrgConfig = false
  ) => {
    try {
      if (!selectedOrg || !selectedOrg.id) {
        message.error("No organization selected");
        return;
      }

      if (!calledFromOrgConfig) {
        message.loading("Uploading creatives...", 0);
      }

      const chunkSize = 10;
      const totalChunks = Math.ceil(medias.length / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const startIndex = i * chunkSize;
        const endIndex = startIndex + chunkSize;
        const chunkMedias = medias.slice(startIndex, endIndex);

        const data = { media: { create: chunkMedias } };

        const result = await createManyOrgMedia({
          variables: {
            id: selectedOrg.id,
            data,
          },
        });

        if (result && result.data) {
          continue;
        } else {
          throw new Error("Failed to upload media");
        }
      }

      if (!calledFromOrgConfig) {
        message.destroy();
        if (invalidMedias.length) {
          message.error(
            `Failed to upload ${invalidMedias.length} creative(s).`
          );
        }
        message.success(`${medias.length} Creative(s) successfully uploaded`);
      }
    } catch (error) {
      if (!calledFromOrgConfig) {
        message.destroy();
        message.error(`Failed to upload creatives: ${error.message || 'Unknown error'}`);
      }
      console.error('Bulk upload error:', error);
      throw error;
    }
  };

  const onDeleteAdgroup = async ids => {
    try {
      message.loading("Action in progress..", 0);
      setOnLoadingDeleteAdGroup(true);

      const results = ids.map(id =>
        deleteAdGroupCreatives({ variables: { id } })
      );

      const values = await Promise.all(results);

      message.destroy();
      message.success(`${values.length} ad group${values.length === 1 ? '' : 's'} successfully deleted.`);
      setOnLoadingDeleteAdGroup(false);
    } catch (err) {
      message.destroy();
      message.error("Failed to delete ad groups.");
    }
  };

  const updateAdGroup = async ({ adGroup, type }) => {
    try {
      const { id, name, medias } = adGroup;

      await updateAdGroupCreatives({
        variables: {
          id,
          data: {
            name,
            medias: { [type]: medias.map(id => ({ id })) },
          },
        },
      });
    } catch (error) {
      console.error('Error updating ad group:', error);
      throw error; // Re-throw so calling component can handle it
    }
  };

  useEffect(() => {
    setSelectedOrg({ ...loggedInOrg });
  }, [loggedInOrg]);


  // Enhanced tab change handlers using centralized navigation
  const handleMainTabChange = tab => {
    // Use default subtab for the selected tab
    const subtab = tab === 'videoAds' ? 'videos' : 'displayAdsUnits';
    navigateToTab(tab, subtab, 1); // Always reset to page 1 when switching main tabs
  };

  const handleDisplaySubTabChange = subtab => {
    navigateToTab('displayAds', subtab, 1); // Reset to page 1 when switching subtabs
  };

  const handleVideoSubTabChange = subtab => {
    navigateToTab('videoAds', subtab, 1); // Reset to page 1 when switching subtabs
  };

  // NEW: Enhanced pagination handler with state preservation
  const handlePageChange = (page) => {
    updateCurrentPagination(page);
    navigateToPage(page);
  };

  const renderDisplayContent = () => {
    const displayTabItems = [
      {
        key: 'displayAdsUnits',
        label: <span>Display Ad Units</span>,
        children: (
          <div style={{ width: '100%', position: 'relative' }}>
            <Spin
              spinning={loadingDisplayCreatives}
              tip="Loading creatives..."
              style={{ width: '100%', display: 'block' }}
            >
              <>
                <CreativeTab
                  onChangeStateValue={onChangeStateValue}
                  creativeName={creativeName}
                  setCreativeName={setCreativeName}
                  onDeleteMedias={onDeleteMedias}
                  onLoadingDeleteMedias={onLoadingDeleteMedias}
                  ids={ids}
                  onCreateAdgroup={onCreateAdgroup}
                  setIds={setIds}
                  setRenewedCreatives={setRenewedCreatives}
                  creativeKeys={creativeKeys}
                  bulkCreateOrgMedia={bulkCreateOrgMedia}
                  renewedCreatives={renewedCreatives}
                  searchText={searchText}
                  handleSearch={handleSearch}
                  handleAssignAdUnits={handleAssignAdUnits}
                  assigning={assigning}
                  onLoadingStateChange={setLoadingDisplayCreatives}
                />
                <CreativeTable
                  orgMedias={renewedCreatives}
                  ids={ids}
                  setIds={setIds}
                  setCreativeKeys={setCreativeKeys}
                  searchText={searchText}
                  handleRowClick={handleRowClick}
                  currentPage={currentState.pageNumber}
                  onPageChange={handlePageChange}
                  activeTab={currentState.tab}
                  activeSubTab={currentState.subtab}
                />
              </>
            </Spin>
          </div>
        )
      },
      {
        key: 'adGroups',
        label: <span>Display Ad Groups</span>,
        children: (
          <CreativeAdGroupTable
            setDefaultAmplifyCreatives={setDefaultAmplifyCreatives}
            adGroupCreatives={adGroupCreatives}
            onDeleteAdgroup={onDeleteAdgroup}
            amplify={amplify}
            selectedOrg={selectedOrg}
            updateAdGroup={updateAdGroup}
            orgMedias={renewedCreatives}
            setIds={setIds}
            setCreativeKeys={setCreativeKeys}
            currentPage={currentState.pageNumber}
            onPageChange={handlePageChange}
          />
        )
      }
    ];

    return (
      <Spin
        spinning={assigning || onLoadingDeleteMedias || onLoadingDeleteAdGroup}
        tip="Loading..."
        size="large"
      >
        <Tabs
          activeKey={currentState.subtab}
          onChange={handleDisplaySubTabChange}
          tabPosition="left"
          items={displayTabItems}
        />
      </Spin>
    );
  };

  const renderVideoContent = () => {
    const videoTabItems = [
      {
        key: 'videos',
        label: <span>Video Ad Units</span>,
        children: (
          <>
            <VideoTab
              videoCreativeIds={videoCreativeIds}
              videoCreativeKeys={videoCreativeKeys}
              setVideoCreativeIds={setVideoCreativeIds}
              setVideoCreativeKeys={setVideoCreativeKeys}
              onCreateAdgroup={onCreateAdgroup}
              onDeleteMedias={onDeleteMedias}
              refetchVideoMedias={refetchVideoMedias}
              searchText={searchText}
              handleSearch={handleSearch}
              bulkCreateOrgMedia={bulkCreateOrgMedia}
            />
            <VideoTable
              orgMedias={orgVideoMedias}
              videoCreativeIds={videoCreativeIds}
              setVideoCreativeIds={setVideoCreativeIds}
              setVideoCreativeKeys={setVideoCreativeKeys}
              updateOrgMedia={updateOrgMedia}
              refetchVideoMedias={refetchVideoMedias}
              searchText={searchText}
              currentPage={currentState.pageNumber}
              onPageChange={handlePageChange}
              activeTab={currentState.tab}
              activeSubTab={currentState.subtab}
            />
          </>
        )
      },
      {
        key: 'adVideoGroups',
        label: <span>Video Ad Groups</span>,
        children: (
          <AdVideosGroupTable
            amplify={amplify}
            selectedOrg={selectedOrg}
            adVideosGroups={adGroupCreatives}
            onDeleteAdgroup={onDeleteAdgroup}
            updateAdGroup={updateAdGroup}
            orgMedias={orgVideoMedias}
            setCreativeKeys={setVideoCreativeKeys}
            currentPage={currentState.pageNumber}
            onPageChange={handlePageChange}
          />
        )
      }
    ];

    return (
      <Spin
        spinning={assigning || onLoadingDeleteMedias || onLoadingDeleteAdGroup}
        tip="Loading..."
        size="large"
      >
        <Tabs
          activeKey={currentState.subtab}
          onChange={handleVideoSubTabChange}
          tabPosition="left"
          className="creative-card"
          items={videoTabItems}
        />
      </Spin>
    );
  };

  // NEW: Custom header override styles
  const customHeaderOverrides = {
    padding: "1% 3% 0% ",
  };

  // NEW: Don't render if route is invalid (validation hook will handle redirect)
  if (!isRouteValid) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Validating route...</p>
      </div>
    );
  }

  // NEW: Don't render if context hasn't loaded yet (prevents undefined errors on refresh)
  if (!loggedInOrg || !getAdGroupsByMedia || !deleteAdGroupCreatives) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading creatives...</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      {/* NEW: Add breadcrumb navigation */}
      
      <PageHeaderLayout
        titleIcon={<AppstoreAddOutlined />}
        title={"Creatives"}
        style={customHeaderOverrides}
        tabContent={
          <div style={{ width: "100%" }}>
            <Tabs
              activeKey={currentState.tab}
              onChange={handleMainTabChange}
              className="creatives-tabs"
              items={[
                {
                  key: 'displayAds',
                  label: <span>Display Ads</span>
                },
                {
                  key: 'videoAds',
                  label: <span>Video Ads</span>
                }
              ]}
            />
          </div>
        }
      />
      <PageContentLayout>
        <Card className="creative-header-card">
          <Row xs={24} xl={24}>
            <Title level={5}>
              {isDisplayAds ? "DISPLAY ADS" : "VIDEO ADS"}
            </Title>
          </Row>
        </Card>
        <Card>
          {isDisplayAds ? renderDisplayContent() : null}
          {isVideoAds ? renderVideoContent() : null}
        </Card>
      </PageContentLayout>
    </React.Fragment>
  );
};

export default LibraryCreativesView;